import { useEffect, useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { dashboardApi } from '../services/api'
import wsService from '../services/websocket'

/**
 * Dashboard hero tile — single-glance answer to "how healthy is my
 * fleet right now?". Backed by GET /api/v1/dashboard/fleet-posture.
 *
 * Re-fetches whenever the pipeline broadcasts a lifecycle event so
 * the number moves in sync with the rest of the dashboard's live
 * updates. Debounced to avoid hammering the API during bursty runs.
 */

const STATUS_STYLES = {
  ok: {
    ring: 'ring-emerald-400/40',
    gradient: 'from-emerald-500/20 via-emerald-500/5 to-transparent',
    scoreText: 'text-emerald-300',
    gradeBg: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200',
    icon: ShieldCheckIcon,
    iconClass: 'text-emerald-400',
  },
  warn: {
    ring: 'ring-yellow-400/40',
    gradient: 'from-yellow-500/20 via-yellow-500/5 to-transparent',
    scoreText: 'text-yellow-300',
    gradeBg: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-200',
    icon: ExclamationTriangleIcon,
    iconClass: 'text-yellow-400',
  },
  bad: {
    ring: 'ring-red-400/40',
    gradient: 'from-red-500/20 via-red-500/5 to-transparent',
    scoreText: 'text-red-300',
    gradeBg: 'bg-red-500/20 border-red-500/50 text-red-200',
    icon: ExclamationTriangleIcon,
    iconClass: 'text-red-400',
  },
}

const METRIC_STATUS_CLASSES = {
  ok: 'text-emerald-300',
  warn: 'text-yellow-300',
  bad: 'text-red-300',
  info: 'text-cyan-300',
}

export default function FleetPostureTile() {
  const [posture, setPosture] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchPosture = useCallback(async () => {
    try {
      const res = await dashboardApi.fleetPosture()
      setPosture(res.data)
      setError(null)
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load fleet posture')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPosture()
  }, [fetchPosture])

  // Live updates: debounced re-fetch on pipeline lifecycle events.
  useEffect(() => {
    let timer = null
    const debouncedRefetch = () => {
      if (timer) return
      timer = setTimeout(() => {
        timer = null
        fetchPosture()
      }, 500)
    }
    const events = [
      'vulnerability_detected',
      'patch_generated',
      'patch_approved',
      'patch_rejected',
      'deployment_started',
      'deployment_completed',
      'deployment_failed',
      'deployment_rolled_back',
      'rollback.triggered',
      'rollback_triggered',
      'pipeline.completed',
    ]
    const offs = events.map((name) => wsService.on(name, debouncedRefetch))
    return () => {
      if (timer) clearTimeout(timer)
      offs.forEach((off) => off?.())
    }
  }, [fetchPosture])

  if (loading && !posture) {
    return (
      <div className="rounded-2xl border border-edge bg-surface-2/60 p-6 min-h-[180px] flex items-center justify-center">
        <ArrowPathIcon className="h-5 w-5 text-text-tertiary animate-spin" />
      </div>
    )
  }

  if (error && !posture) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 text-red-300 text-sm">
        {error}
      </div>
    )
  }

  if (!posture) return null

  const style = STATUS_STYLES[posture.status] || STATUS_STYLES.warn
  const Icon = style.icon

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`relative rounded-2xl border border-edge bg-gradient-to-br ${style.gradient} p-6 ring-1 ring-inset ${style.ring} overflow-hidden`}
    >
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-transparent via-black/0 to-black/10" />

      <div className="relative grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 items-center">
        {/* Score + grade block */}
        <div className="flex items-center gap-4">
          <div className={`relative flex items-center justify-center rounded-full border-2 ${style.ring.replace('ring-', 'border-').replace('/40', '/50')} h-24 w-24 bg-surface-0/40`}>
            <span className={`text-4xl font-bold font-heading ${style.scoreText}`}>
              {posture.score}
            </span>
            <span className="absolute bottom-1.5 right-1.5 text-2xs text-text-tertiary font-mono">/100</span>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`inline-flex items-center justify-center h-8 w-8 rounded-lg border text-lg font-bold font-heading ${style.gradeBg}`}
              >
                {posture.grade}
              </span>
              <div className="flex items-center gap-1.5 text-xs text-text-tertiary uppercase tracking-wider">
                <Icon className={`h-4 w-4 ${style.iconClass}`} />
                Fleet posture
              </div>
            </div>
            <p className="text-sm text-text-primary leading-snug max-w-md">
              {posture.headline}
            </p>
          </div>
        </div>

        {/* Drill-down metrics grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {(posture.metrics ?? []).map((m) => (
            <div
              key={m.key}
              title={m.hint || ''}
              className="rounded-lg border border-edge bg-surface-0/40 px-3 py-2 cursor-help"
            >
              <p className="text-2xs uppercase tracking-wider text-text-tertiary truncate">
                {m.label}
              </p>
              <p
                className={`mt-0.5 text-xl font-bold font-mono ${
                  METRIC_STATUS_CLASSES[m.status] || METRIC_STATUS_CLASSES.info
                }`}
              >
                {m.value}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Factor list (only shown when there are penalties) */}
      {posture.factors && posture.factors.length > 0 && (
        <div className="relative mt-5 pt-4 border-t border-edge/60">
          <p className="text-2xs uppercase tracking-wider text-text-tertiary mb-2">
            Why this score
          </p>
          <div className="flex flex-wrap gap-2">
            {posture.factors.map((f, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 rounded-full border border-edge bg-surface-0/60 px-2.5 py-1 text-2xs text-text-secondary"
              >
                <span className="font-mono text-red-300">{f.delta}</span>
                {f.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  )
}
